import { LitElement, html, css, nothing } from 'lit';
import { PRINT_STYLES } from './print-styles.js';
import { repeat } from 'lit/directives/repeat.js';
import { StoreMixin, store } from '../store.js';
import { api } from '../api.js';
import { navigate, setFilters } from '../router.js';
import { apiParams } from '../lib/filters.js';
import { statusColor } from '../lib/palette.js';
import {
  DASHBOARD_PARAM_KEYS,
  dashboardKpis,
  kpiRows,
  pipelineImpact,
  riskProjects,
  riskServices,
  kpiColumns,
  pipelineImpactColumns,
  riskProjectColumns,
  riskServiceColumns,
} from '../lib/dashboard.js';
import { withFormats } from '../lib/table.js';
import { hours as formatHours, percent, count, weekLabel, humanize } from '../lib/format.js';

/** Which vocabulary names an entity of a dimension, for the "narrowed below" note. */
const VOCAB_FOR = {
  service: 'services',
  department: 'departments',
  office: 'offices',
  individual: 'people',
};

const FLAG_LABELS = { slipped: 'slipped', burn_risk: 'burn risk', recurring: 'recurring' };

/**
 * The top of the dashboard (PRD 10.1; Phase 22): the KPI strip — average and peak allocation,
 * the peak week, the overload and bench counts — and beneath it the top risk services, the top
 * risk projects and the pipeline's impact in both modes. Everything is read from one
 * `GET /forecast/summary` response for the window, run and opportunity mode in the filter bar;
 * the component adds no arithmetic beyond ordering and proportion (`lib/dashboard.js`), so a
 * number here is a number the summary said, and the Phase 14 gate already checked the summary
 * against the signals and the ribbon.
 *
 * The strip is the whole company's, as the sketch draws it: the summary has no entity. When the
 * filter bar narrows the ribbon and bridge below to one entity, the card says so rather than
 * letting a company KPI sit unlabelled over a service's chart. A top risk service is a link:
 * clicking it narrows the charts to that service.
 *
 * Rows are keyed (`repeat` on the entity id), so a refetch updates a surviving row in place.
 */
class DashboardSummary extends StoreMixin(LitElement) {
  static properties = {
    _data: { state: true },
    _loading: { state: true },
    _error: { state: true },
    _tableOpen: { state: true },
  };

  constructor() {
    super();
    this._data = null;
    this._loading = false;
    this._error = null;
    this._tableOpen = false;
    this._requestKey = null;
  }

  static styles = [
    PRINT_STYLES,
    css`
      :host {
        display: block;
      }
      .card {
        background: var(--surface);
        border: 1px solid var(--border-soft);
        border-radius: var(--radius-card);
        box-shadow: var(--shadow-card);
        padding: 20px 24px 18px;
      }
      .head {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 6px 18px;
        margin-bottom: 14px;
      }
      .eyebrow {
        font-family: var(--font-eyebrow);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: var(--ink-muted);
      }
      h2 {
        margin: 2px 0 0;
        font-size: 20px;
        line-height: 1.2;
        color: var(--ink-strong);
      }
      h3 {
        margin: 0 0 10px;
        font-size: 14px;
        line-height: 1.3;
        color: var(--ink-strong);
      }
      .sub {
        color: var(--ink-muted);
        font-size: 12.5px;
      }
      .note {
        margin: 10px 0 0;
        font-size: 12.5px;
        color: var(--ink-muted);
      }
      .note.warn {
        color: var(--status-overloaded);
        font-weight: 600;
      }
      .state {
        padding: 36px 0;
        text-align: center;
        color: var(--ink-muted);
      }
      .state.error {
        color: var(--status-overloaded);
        font-weight: 600;
      }

      /* The KPI strip */
      .strip {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 12px;
        margin: 0;
        padding: 0;
        list-style: none;
      }
      .tile {
        border: 1px solid var(--border-soft);
        border-radius: var(--radius-card);
        background: var(--surface-2);
        padding: 12px 14px 11px;
        min-width: 0;
      }
      .tile .l {
        font-size: 11.5px;
        color: var(--ink-muted);
        margin-bottom: 4px;
      }
      .tile .n {
        display: flex;
        align-items: baseline;
        flex-wrap: wrap;
        gap: 6px 10px;
        font-size: 26px;
        font-weight: 700;
        line-height: 1.1;
        color: var(--ink-strong);
        font-variant-numeric: tabular-nums;
      }
      .tile .n .unit {
        font-size: 13px;
        font-weight: 600;
        color: var(--ink-muted);
      }
      .tile .d {
        margin-top: 6px;
        font-size: 12px;
        color: var(--ink-muted);
        line-height: 1.45;
      }
      .tile .d b {
        color: var(--ink);
        font-weight: 600;
        font-variant-numeric: tabular-nums;
      }

      /* The three panels */
      .panels {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
        gap: 18px;
        margin-top: 18px;
      }
      .panel {
        min-width: 0;
        border-top: 1px solid var(--border-soft);
        padding-top: 12px;
      }
      ol.risk {
        list-style: none;
        margin: 0;
        padding: 0;
        display: grid;
        gap: 9px;
      }
      ol.risk li {
        display: grid;
        grid-template-columns: 1fr auto;
        gap: 2px 10px;
        font-size: 13px;
        min-width: 0;
      }
      .who {
        display: flex;
        align-items: baseline;
        gap: 6px;
        min-width: 0;
        color: var(--ink);
      }
      .who .rank {
        flex: none;
        font-size: 11px;
        color: var(--ink-faint);
        font-variant-numeric: tabular-nums;
        width: 1.2em;
      }
      .who .name {
        font-weight: 600;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .who button.name {
        font: inherit;
        font-weight: 600;
        color: var(--link);
        background: none;
        border: 0;
        padding: 0;
        cursor: pointer;
        text-align: left;
      }
      .who button.name:hover {
        text-decoration: underline;
      }
      .val {
        font-weight: 700;
        color: var(--ink-strong);
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
        text-align: right;
      }
      .meter {
        grid-column: 1 / -1;
        height: 5px;
        border-radius: 3px;
        background: var(--surface-sunken);
        overflow: hidden;
      }
      .meter > span {
        display: block;
        height: 100%;
        border-radius: 3px;
        background: var(--ink-muted);
      }
      .meta {
        grid-column: 1 / -1;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 3px 10px;
        font-size: 11.5px;
        color: var(--ink-muted);
      }
      .tag {
        font-size: 10.5px;
        font-weight: 600;
        letter-spacing: 0.02em;
        text-transform: uppercase;
        color: var(--ink-muted);
        border: 1px solid var(--border);
        border-radius: var(--radius-pill);
        padding: 0 6px;
        line-height: 15px;
      }
      .tag.slipped,
      .tag.burn_risk {
        color: var(--status-overloaded);
        border-color: currentColor;
      }
      .empty {
        font-size: 12.5px;
        color: var(--ink-muted);
        margin: 0;
      }

      /* Pipeline impact */
      .impact {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 10px;
      }
      .impact .mode {
        border: 1px solid var(--border-soft);
        border-radius: var(--radius-card);
        padding: 10px 12px;
        background: var(--surface-2);
      }
      .impact .mode.in-use {
        border-color: var(--ink-muted);
      }
      .impact .mode .l {
        font-size: 11.5px;
        color: var(--ink-muted);
        display: flex;
        justify-content: space-between;
        gap: 6px;
      }
      .impact .mode .n {
        font-size: 22px;
        font-weight: 700;
        color: var(--ink-strong);
        font-variant-numeric: tabular-nums;
        line-height: 1.15;
        margin-top: 2px;
      }
      .impact .mode .h {
        font-size: 12px;
        color: var(--ink-muted);
        font-variant-numeric: tabular-nums;
      }
      .impact .mode .in-use-mark {
        font-family: var(--font-eyebrow);
        font-size: 9.5px;
        font-weight: 700;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        color: var(--ink);
      }

      details.table {
        margin-top: 16px;
        border-top: 1px solid var(--border-soft);
        padding-top: 12px;
      }
      details.table summary {
        cursor: pointer;
        font-size: 13px;
        font-weight: 600;
        color: var(--link);
        list-style: none;
      }
      details.table summary::-webkit-details-marker {
        display: none;
      }
      details.table summary::before {
        content: '▸ ';
        color: var(--ink-muted);
      }
      details.table[open] summary::before {
        content: '▾ ';
      }
      details.table data-table {
        margin-top: 12px;
      }
    `,
  ];

  connectedCallback() {
    super.connectedCallback();
    // Fetch off the store, not off the render cycle (see demand-ribbon.js).
    this._unsubscribeFetch = store.subscribe(() => this._maybeFetch());
    this._maybeFetch();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._unsubscribeFetch?.();
  }

  // -------------------------------------------------------------------------------------------
  // Data

  /** The request this card wants for the current filters, or null when there is no run. */
  _params() {
    const { filters, run } = this.appState;
    if (!run) return null;
    return apiParams(filters, DASHBOARD_PARAM_KEYS);
  }

  async _maybeFetch() {
    const params = this._params();
    const key = params ? JSON.stringify(params) : null;
    if (key === this._requestKey) return;
    this._requestKey = key;
    if (!params) {
      this._data = null;
      return;
    }
    this._loading = true;
    this._error = null;
    try {
      const data = await api.get('/forecast/summary', params);
      if (this._requestKey !== key) return; // a newer request has taken over
      this._data = data;
    } catch (err) {
      if (this._requestKey !== key) return;
      this._error = err.message;
      this._data = null;
    } finally {
      if (this._requestKey === key) this._loading = false;
    }
  }

  get _kpis() {
    return this._data ? dashboardKpis(this._data) : null;
  }

  get _services() {
    return this._data ? riskServices(this._data) : { rows: [], api_ordered: true };
  }

  get _projects() {
    return this._data ? riskProjects(this._data) : { rows: [], api_ordered: true };
  }

  get _impact() {
    return this._data ? pipelineImpact(this._data) : null;
  }

  /** Narrow the charts below to one service; the strip stays the company's and says so. */
  _filterToService(entityId) {
    setFilters({ dimension: 'service', entity: entityId });
  }

  /**
   * Leave for one project's panel. The two risk lists read alike and act differently: a service
   * is a dimension of this page, so its name narrows the charts below; a project is not, and the
   * question a planner has on seeing one here — why is this project in overloaded weeks — is
   * the question Project Intelligence answers, so its name leaves.
   *
   * The id is the run's own `entity_id`, which is a `forecast_projects` row of this run whether
   * the row is a project or a recurring entity, so the panel this lands on can always be built.
   */
  _openProject(projectId) {
    navigate(`/projects/${projectId}`);
  }

  /** What the filter bar has narrowed the ribbon and bridge to, if anything. */
  _narrowedTo() {
    const { filters, vocabularies } = this.appState;
    if (!filters.entity || filters.dimension === 'company') return null;
    const vocab = vocabularies?.[VOCAB_FOR[filters.dimension]] ?? [];
    const found = vocab.find((v) => (v.value ?? v.id) === filters.entity);
    return {
      dimension: filters.dimension,
      label: found?.label ?? found?.display_name ?? filters.entity,
    };
  }

  // -------------------------------------------------------------------------------------------
  // Render helpers

  _badge(band, extra = {}) {
    if (!band) return nothing;
    return html`<status-badge state=${band} ?compact=${extra.compact ?? false}></status-badge>`;
  }

  _tiles() {
    const k = this._kpis;
    if (!k) return nothing;
    const a = k.average;
    const p = k.peak;
    const o = k.overload;
    const b = k.bench;
    const bw = a.band_weeks;
    return html`
      <ul class="strip" aria-label="Key figures for the window">
        <li class="tile" data-kpi="average">
          <div class="l">Company allocation, average over ${count(a.weeks_count)} weeks</div>
          <div class="n">
            ${a.utilization === null ? '—' : percent(a.utilization)} ${this._badge(a.band)}
          </div>
          <div class="d">
            <b>${formatHours(a.demand_hours)}</b> demand against
            <b>${formatHours(a.capacity_hours)}</b> capacity ·
            ${
              a.headroom_hours < 0
                ? html`<b>${formatHours(-a.headroom_hours)}</b> over`
                : html`<b>${formatHours(a.headroom_hours)}</b> headroom`
            }<br />
            ${count(bw.overloaded)} overloaded · ${count(bw.caution)} caution · ${count(bw.healthy)}
            healthy · ${count(bw.underload)} under
            weeks${
              a.weeks_without_capacity
                ? html` · ${count(a.weeks_without_capacity)} without capacity`
                : nothing
            }
          </div>
        </li>
        <li class="tile" data-kpi="peak">
          <div class="l">
            Peak${p.week_start ? html`, week of <b>${weekLabel(p.week_start)}</b>` : nothing}
          </div>
          <div class="n">
            ${p.utilization === null ? '—' : percent(p.utilization)} ${this._badge(p.band)}
          </div>
          <div class="d">
            ${
              p.week_start
                ? html`<b>${formatHours(p.demand_hours)}</b> demand against
                    <b>${formatHours(p.capacity_hours)}</b> ·
                    ${
                      p.over_hours > 0
                        ? html`<b>${formatHours(p.over_hours)}</b> over the line`
                        : html`<b>${formatHours(p.headroom_hours)}</b> headroom`
                    }<br />
                    <b>${count(p.weeks_over)}</b> of ${count(a.weeks_count)} weeks over capacity`
                : 'No week with demand or capacity in this window.'
            }
          </div>
        </li>
        <li class="tile" data-kpi="overload">
          <div class="l">Overload alerts</div>
          <div class="n">
            ${count(o.services)}
            <span class="unit">${o.services === 1 ? 'service' : 'services'}</span>
          </div>
          <div class="d">
            <b>${count(o.people)}</b> ${o.people === 1 ? 'person' : 'people'} ·
            <b>${count(o.departments)}</b>
            ${o.departments === 1 ? 'department' : 'departments'}<br />
            <b>${count(o.windows)}</b> ${o.windows === 1 ? 'window' : 'windows'} over the line ·
            <b>${count(o.deadlines_past)}</b> ${o.deadlines_past === 1 ? 'deadline' : 'deadlines'}
            past
          </div>
        </li>
        <li class="tile" data-kpi="bench">
          <div class="l">Bench</div>
          <div class="n">
            ${count(b.services)}
            <span class="unit">${b.services === 1 ? 'service' : 'services'}</span>
          </div>
          <div class="d">
            <b>${count(b.people)}</b> ${b.people === 1 ? 'person' : 'people'} ·
            <b>${count(b.departments)}</b> ${b.departments === 1 ? 'department' : 'departments'}
            under-utilized<br />
            <b>${formatHours(b.hours)}</b> unallocated across <b>${count(b.weeks)}</b>
            ${b.weeks === 1 ? 'week' : 'weeks'} under the band
          </div>
        </li>
      </ul>
    `;
  }

  _servicePanel() {
    const { rows, api_ordered } = this._services;
    return html`
      <section class="panel" data-panel="services">
        <h3>Top risk services</h3>
        ${
          rows.length === 0
            ? html`<p class="empty">No service is over the line in this window.</p>`
            : html`
                <ol class="risk">
                  ${repeat(
                    rows,
                    (r) => r.entity_id,
                    (r) => html`
                      <li data-entity=${r.entity_id}>
                        <span class="who">
                          <span class="rank">${r.rank}</span>
                          <button
                            class="name"
                            title="Narrow the ribbon and bridge to ${r.label}"
                            @click=${() => this._filterToService(r.entity_id)}
                          >
                            ${r.label}
                          </button>
                        </span>
                        <span class="val">${formatHours(r.hours_over)} over</span>
                        <span class="meter" aria-hidden="true">
                          <span
                            style=${`width: ${Math.max(2, Math.round(r.share_of_top * 100))}%; background: ${statusColor(r.severity)}`}
                          ></span>
                        </span>
                        <span class="meta">
                          <status-badge state=${r.severity} compact></status-badge>
                          ${
                            r.peak_over_ratio === null
                              ? nothing
                              : html`<span
                                  >peak ${percent(r.peak_utilization)}
                                  (${r.peak_over_ratio >= 0 ? '+' : ''}${percent(r.peak_over_ratio)})</span
                                >`
                          }
                          <span>from ${weekLabel(r.first_week)}</span>
                          <span
                            >${count(r.weeks_over)} ${r.weeks_over === 1 ? 'week' : 'weeks'}</span
                          >
                        </span>
                      </li>
                    `,
                  )}
                </ol>
              `
        }
        ${
          api_ordered
            ? nothing
            : html`<p class="note warn">
                The API listed these services out of hours-over order — report this.
              </p>`
        }
      </section>
    `;
  }

  _projectPanel() {
    const { rows, api_ordered } = this._projects;
    return html`
      <section class="panel" data-panel="projects">
        <h3>Top risk projects</h3>
        ${
          rows.length === 0
            ? html`<p class="empty">No project has hours in an overloaded service-week.</p>`
            : html`
                <ol class="risk">
                  ${repeat(
                    rows,
                    (r) => r.project_id,
                    (r) => html`
                      <li data-project=${r.project_id}>
                        <span class="who">
                          <span class="rank">${r.rank}</span>
                          <button
                            class="name"
                            title="Open ${r.label} in Project Intelligence"
                            @click=${() => this._openProject(r.project_id)}
                          >
                            ${r.label}
                          </button>
                        </span>
                        <span class="val">${formatHours(r.hours_in_overloaded_weeks)}</span>
                        <span class="meter" aria-hidden="true">
                          <span
                            style=${`width: ${Math.max(2, Math.round(r.share_of_top * 100))}%`}
                          ></span>
                        </span>
                        <span class="meta">
                          ${r.client ? html`<span>${r.client}</span>` : nothing}
                          <span>${humanize(r.canonical_type)}</span>
                          <span
                            >${count(r.services)} ${r.services === 1 ? 'service' : 'services'} ·
                            ${count(r.weeks)} ${r.weeks === 1 ? 'week' : 'weeks'}</span
                          >
                          ${r.flags.map((f) => html`<span class=${`tag ${f}`}>${FLAG_LABELS[f]}</span>`)}
                        </span>
                      </li>
                    `,
                  )}
                </ol>
              `
        }
        ${
          api_ordered
            ? nothing
            : html`<p class="note warn">
                The API listed these projects out of hours order — report this.
              </p>`
        }
      </section>
    `;
  }

  _impactPanel() {
    const im = this._impact;
    if (!im) return nothing;
    const excluded = im.in_use_mode === 'none';
    return html`
      <section class="panel" data-panel="pipeline">
        <h3>Pipeline impact</h3>
        <div class="impact">
          ${im.rows.map(
            (r) => html`
              <div class=${`mode ${r.in_use ? 'in-use' : ''}`} data-mode=${r.mode}>
                <div class="l">
                  <span>${humanize(r.mode)}</span>
                  ${r.in_use ? html`<span class="in-use-mark">on the charts</span>` : nothing}
                </div>
                <div class="n">${r.share === null ? '—' : `+${percent(r.share)}`}</div>
                <div class="h">${formatHours(r.hours)} of ${formatHours(im.capacity_hours)}</div>
              </div>
            `,
          )}
        </div>
        <p class="note">
          ${count(im.opportunities)} open ${im.opportunities === 1 ? 'deal' : 'deals'} with hours in
          the window.
          ${
            excluded
              ? 'The ribbon and bridge exclude the pipeline (opportunity mode None).'
              : `The ribbon and bridge draw the ${im.in_use_mode} figure.`
          }
        </p>
      </section>
    `;
  }

  // -------------------------------------------------------------------------------------------
  // Tables

  get _kpiColumns() {
    // The specification is shared with the server's CSV (lib/dashboard.js); the drawing is here.
    return withFormats(kpiColumns(), {
      byKey: {
        value: (v, row) =>
          row.unit === 'ratio'
            ? percent(v)
            : row.unit === 'hours'
              ? formatHours(v, { exact: true })
              : count(v),
      },
    });
  }

  get _serviceColumns() {
    return withFormats(riskServiceColumns(), {
      byUnit: {
        hours: (v) => formatHours(v, { exact: true }),
        percent: (v) => percent(v),
        week: (v) => weekLabel(v),
      },
      byKey: {
        severity: (v) => html`<status-badge state=${v} compact></status-badge>`,
      },
    });
  }

  get _projectColumns() {
    return withFormats(riskProjectColumns(), {
      byUnit: {
        hours: (v) => formatHours(v, { exact: true }),
        percent: (v) => percent(v),
        week: (v) => weekLabel(v),
      },
      byKey: {
        client: (v) => v ?? '—',
        canonical_type: (v) => humanize(v),
        confidence_tier: (v) => humanize(v),
        source_mode: (v) => humanize(v),
        flags: (v) => (v ?? []).map((f) => FLAG_LABELS[f] ?? f).join(', '),
      },
    });
  }

  get _impactColumns() {
    return withFormats(pipelineImpactColumns(), {
      byUnit: {
        hours: (v) => formatHours(v, { exact: true }),
        percent: (v) => percent(v),
        week: (v) => weekLabel(v),
      },
      byKey: {
        mode: (v) => humanize(v),
        in_use: (v) => (v ? 'yes' : ''),
      },
    });
  }

  _tables() {
    const d = this._data;
    const im = this._impact;
    const scope = this._scopeLabel();
    return html`
      <details
        class="table"
        ?open=${this._tableOpen}
        @toggle=${(e) => (this._tableOpen = e.target.open)}
      >
        <summary>Table view</summary>
        <data-table
          caption=${`Key figures — ${scope}`}
          export-name="dashboard-kpis"
          page-size="20"
          dense
          .columns=${this._kpiColumns}
          .rows=${kpiRows(d)}
        ></data-table>
        <data-table
          caption=${`Top risk services — ${scope}`}
          export-name="dashboard-risk-services"
          page-size="10"
          dense
          .columns=${this._serviceColumns}
          .rows=${this._services.rows}
        ></data-table>
        <data-table
          caption=${`Top risk projects — ${scope}`}
          export-name="dashboard-risk-projects"
          page-size="10"
          dense
          .columns=${this._projectColumns}
          .rows=${this._projects.rows}
        ></data-table>
        <data-table
          caption=${`Pipeline impact — ${scope} · ${count(im.opportunities)} deals · capacity ${formatHours(im.capacity_hours, { exact: true })}`}
          export-name="dashboard-pipeline-impact"
          page-size="5"
          dense
          .columns=${this._impactColumns}
          .rows=${im.rows}
        ></data-table>
      </details>
    `;
  }

  // -------------------------------------------------------------------------------------------
  // Render

  _scopeLabel() {
    const d = this._data;
    if (!d) return '';
    return `Whole company · ${d.weeks_count} weeks from ${weekLabel(d.from)}`;
  }

  _subtitle() {
    const d = this._data;
    if (!d) return '';
    return `Whole company · ${d.weeks_count} weeks from ${weekLabel(d.from)} to ${weekLabel(d.to)} · pipeline ${d.pipeline_mode}`;
  }

  _notes() {
    const d = this._data;
    const k = this._kpis;
    if (!d || !k) return nothing;
    const narrowed = this._narrowedTo();
    const o = k.overload;
    return html`
      ${
        narrowed
          ? html`<p class="note" data-note="narrowed">
              These figures are the whole company's. The ribbon and bridge below are narrowed to
              <b>${narrowed.label}</b> (${narrowed.dimension}); clear the entity in the filter bar
              to line them up.
            </p>`
          : nothing
      }
      ${
        o.matches_request
          ? nothing
          : html`<p class="note" data-note="signals-mode">
              The overload and bench counts come from the run's stored signals, computed with the
              pipeline ${o.pipeline_mode}; the allocation figures and the charts use
              ${d.pipeline_mode}.
            </p>`
      }
    `;
  }

  render() {
    const { run } = this.appState;
    const d = this._data;

    let body;
    if (!run) body = html`<div class="state">No forecast run yet — nothing to summarise.</div>`;
    else if (this._error) body = html`<div class="state error">${this._error}</div>`;
    else if (!d && this._loading) body = html`<div class="state">Loading the summary…</div>`;
    else if (!d) body = html`<div class="state">No summary for this window.</div>`;
    else
      body = html`
        ${this._tiles()} ${this._notes()}
        <div class="panels">
          ${this._servicePanel()} ${this._projectPanel()} ${this._impactPanel()}
        </div>
        ${this._tables()}
      `;

    return html`
      <div class="card" aria-busy=${this._loading ? 'true' : 'false'}>
        <div class="head">
          <div>
            <div class="eyebrow">Dashboard</div>
            <h2>Where is the pressure?</h2>
            <div class="sub">${this._subtitle()}</div>
          </div>
        </div>
        ${body}
      </div>
    `;
  }
}

customElements.define('dashboard-summary', DashboardSummary);
